import clsx from "clsx";
import React, { ReactElement, useState } from "react";
import { Event, Permission, Timeslot } from "types";
import { allowGuideAndInventoryAssign } from "../../../../models/Event";
import { EmptyResourceDisplay } from "../EmptyResourceDisplay";
import {
    DashboardResourceType,
    getColor,
    ResourceDisplay,
    ResourceDisplayElement,
    ResourceIcon,
} from "../ResourceDisplay";
import { AssignGuidePopover } from "./AssignGuidePopover";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useResource } from "@/hooks/useResource";
import { useGuides } from "@/hooks/useSellerResource";

interface DashboardGuideResourceProps {
    icon: ReactElement;
    className?: string;
    timeslot: Timeslot;
    displayType?: DisplayType;
}

export enum DisplayType {
    FULL = "full",
    COMPACT = "compact",
}

export const DashboardGuideResource = ({
    icon,
    className,
    timeslot,
    displayType = DisplayType.FULL,
}: DashboardGuideResourceProps) => {
    const seller = useSeller();
    const { isGranted } = useAuthorization();

    const { data: event } = useResource<Event>(["events", timeslot.event?.id]);
    const { guides } = useGuides(null, { enabled: true });

    const needsResourceAssignment = event ? event.guides.length === 0 : true;

    const [showAssignGuide, setShowAssignGuide] = useState(false);

    const handleClick = (e: any) => {
        e.stopPropagation?.();
        setShowAssignGuide(true);
    };

    const isCompact = displayType === DisplayType.COMPACT;
    const isNotAssignable = !isGranted(Permission.MODIFY_RESOURCES_GUIDE) || guides?.length === 0;

    if ((needsResourceAssignment && isNotAssignable) || !allowGuideAndInventoryAssign(seller, event, timeslot)) {
        return <EmptyResourceDisplay isCompact={isCompact} needsResourceAssignment={needsResourceAssignment} />;
    }

    const ResourceContent = (
        <ResourceDisplayElement type={DashboardResourceType.GUIDES} event={event} isCompact={isCompact} />
    );

    return (
        <span className={clsx("hover:cursor-pointer", "max-w-fit")} onClick={handleClick}>
            <ResourceDisplay
                shouldShowDot
                icon={<ResourceIcon event={event} icon={icon} type={DashboardResourceType.GUIDES} />}
                color={getColor(event ?? null, needsResourceAssignment)}
                className={clsx(
                    isCompact ? "inline-block" : "w-24 truncate xl:w-28 2xl:w-32",
                    isCompact && !needsResourceAssignment && "pl-1",
                    className,
                )}
            >
                {isGranted(Permission.RESOURCES_GUIDES_ALL_VIEW) ? (
                    <AssignGuidePopover
                        events={event ? [event] : []}
                        target={ResourceContent}
                        timeslots={event ? [] : [timeslot]}
                        isVisible={showAssignGuide}
                        onClose={(): void => {
                            setShowAssignGuide(false);
                        }}
                    />
                ) : (
                    ResourceContent
                )}
            </ResourceDisplay>
        </span>
    );
};
